import { InvoiceDiscountType } from "enums/InvoiceDiscountType";
import { InvoicePaymentTerms } from "enums/InvoicePaymentTerms";
import { LocalImageAsset } from "hooks/useAttachmentAndAssetHandling";
import moment from "moment";
import { formatCashFull } from "utils/formatCash";
import { contactName, fullName } from "utils/fullName";
import { InvoiceStatus } from "enums/InvoiceStatus";
import { CustomFieldsRecord } from "hooks/customFields.types";

/**
 * The only currency an invoice has. There is no `currency` field on the
 * GraphQL `Invoice` type, none in the REST payload, and no query selects one,
 * so every amount on this surface is this. Stated rather than left to
 * `formatCashFull`'s default parameter, which only covers `undefined`: one
 * caller passing an explicit `null` would blank every figure silently.
 */
export const INVOICE_CURRENCY = "USD";

/** A summary field holds what was typed until the invoice is saved. */
type TypedFigure = number | string;

export interface FormikInvoice {
  id?: string;
  invoiceNumber: string;
  invoiceName: string;
  purchaseOrderId: string;
  primaryRecipient: any;
  secondaryRecipients: any;
  paymentTerms: InvoicePaymentTerms;
  invoiceDate: Date;
  sentDate: Date | null;
  dueDate: Date | null;
  subtotal: number;
  tax: TypedFigure;
  taxType: InvoiceDiscountType;
  taxAmount: number;
  discount: TypedFigure;
  discountType: InvoiceDiscountType;
  discountAmount: number;
  serviceFee: TypedFigure;
  serviceFeeType: InvoiceDiscountType;
  serviceFeeAmount: number;
  total: number;
  currency: string;
  attachments: LocalImageAsset[];
  details: InvoiceDetail[];
  status: string;
  sentBy: {
    id: string;
    firstName: string;
    lastName?: string | null;
  } | null;
  submittedBy: {
    id: string;
    firstName: string;
    lastName?: string | null;
  };
  createdAt: Date;
  note: string;
  paidDate: Date | null;
  paidBy: {
    id: string;
    name: string;
  } | null;
  allocationType: {
    key: string;
    name: string;
  } | null;
  allocationName: {
    id: string;
    allocationCode: string;
  } | null;
  transactionId: string | null;
  companyDetails: {
    companyLogoUrl: string;
    companyName: string;
    companyPhone: string;
    companyWebsite: string;
    companyEmail: string;
  } | null;
  sentHistory?: {
    sentDate: Date;
    sentType: string;
    sentBy: {
      id: string;
      firstName: string;
      lastName?: string | null;
    };
  }[];
  estimateActionHistory?: {
    timestamp: Date;
    updatedBy: {
      id: string;
      firstName: string;
      lastName?: string | null;
      businessName: string;
      contactType: string;
    };
    action: string;
    comment?: string;
  }[];
  disputeData: {
    disputeAmount: number;
    disputeId: string;
    disputeStatus: string;
  } | null;
  canceledBy?: {
    id: string;
    firstName: string;
    lastName?: string | null;
  } | null;
  canceledDate?: Date | null;
  linkedTransaction?: {
    id: string;
    type: string;
    amount: number;
    currency: string;
    fees?: { feeType?: string; feeAmount?: number } | null;
  } | null;
  customFields?: CustomFieldsRecord;
}

export const invoiceInitialValues = {
  id: "",
  invoiceNumber: "",
  invoiceName: "",
  purchaseOrderId: "",
  primaryRecipient: null,
  secondaryRecipients: null,
  paymentTerms: InvoicePaymentTerms.IMMEDIATELY,
  invoiceDate: new Date(),
  sentDate: null,
  dueDate: null,
  paidDate: null,
  paidBy: null,
  subtotal: 0,
  discount: 0,
  discountType: InvoiceDiscountType.AMOUNT,
  discountAmount: 0,
  serviceFee: 0,
  serviceFeeType: InvoiceDiscountType.AMOUNT,
  serviceFeeAmount: 0,
  tax: 0,
  taxType: InvoiceDiscountType.AMOUNT,
  taxAmount: 0,
  total: 0,
  currency: INVOICE_CURRENCY,
  attachments: [],
  details: [],
  status: InvoiceStatus.DRAFT,
  createdAt: new Date(),
  note: "",
  allocationType: null,
  allocationName: null,
  submittedBy: {
    id: "",
    firstName: "",
    lastName: "",
  },
  sentBy: null,
  transactionId: null,
  companyDetails: null,
  sentHistory: [],
  estimateActionHistory: [],
  disputeData: null,
  linkedTransaction: null,
  canceledBy: null,
  canceledDate: null,
  customFields: {},
};

export const PaymentTermsLabels: { [key in InvoicePaymentTerms]: string } = {
  [InvoicePaymentTerms.NET_30]: "30 Days",
  [InvoicePaymentTerms.NET_60]: "60 Days",
  [InvoicePaymentTerms.NET_90]: "90 Days",
  [InvoicePaymentTerms.IMMEDIATELY]: "Immediately (3 days)",
};

export interface InvoiceDetail {
  id: string;
  invoiceId: string;
  order: number;
  productOrService: string;
  description: string;
  amount: number;
  total: number;
  quantity: number;
}

export function mapInvoiceNodeToTableRow(edge: any) {
  return {
    id: edge.node.id,
    invoiceDate: edge.node.invoiceDate
      ? moment(edge.node.invoiceDate).format("MM/DD/YYYY")
      : "",
    recipient: contactName(edge.node.primaryRecipient),
    recipientEmail: edge.node.primaryRecipient
      ? edge.node.primaryRecipient.email || ""
      : "",
    amount: formatCashFull(edge.node.total, INVOICE_CURRENCY),
    invoice: `${edge.node.invoiceNumber} - ${edge.node.invoiceName}`,
    sentDate: edge.node.sentDate
      ? moment(edge.node.sentDate).format("MM/DD/YYYY")
      : "",
    sentBy: fullName(edge.node.sentBy) || edge.node.sentBy?.businessName || "",
    dueDate: edge.node.dueDate
      ? moment(edge.node.dueDate).format("MM/DD/YYYY")
      : "",
    paidDate: edge.node.paidDate
      ? moment(edge.node.paidDate).format("MM/DD/YYYY")
      : "",
    paymentTerms:
      PaymentTermsLabels[
        edge.node.paymentTerms as keyof typeof PaymentTermsLabels
      ],
    status: edge.node.status,
    microdepositPending: edge.node.microdepositPending,
    primaryRecipient: edge.node.primaryRecipient,
    // The query asks for both and the table reads both: the dispute chip off
    // disputeData, the Refund row action off transaction.status.
    disputeData: edge.node.disputeData,
    transaction: edge.node.transaction,
    // The refund modal opens straight off a row and does arithmetic on these.
    // `amount` above is a formatted string and cannot stand in for `total`.
    total: edge.node.total,
    refundTransactions: edge.node.refundTransactions,
    currency: INVOICE_CURRENCY,
  };
}

export function calculateInvoiceDerivedFields(
  values: FormikInvoice,
  serviceFeeConfig?: any,
) {
  // The summary fields hold what was typed, so a figure can arrive as "10".
  const safeNumber = (val: unknown): number => {
    const n = typeof val === "string" ? parseFloat(val) : val;
    return typeof n === "number" && Number.isFinite(n) ? n : 0;
  };
  const asPercent = (val: unknown) => safeNumber(val) / 100;

  const cardFeePercent = serviceFeeConfig?.cardFeePercent;
  const cardFeeAdderCents = serviceFeeConfig?.cardFeeAdderCents;
  const platformFeePercent = serviceFeeConfig?.platformFeePercent;

  // Round each line total to cents so it survives the server's dollars->cents
  // truncation, and sum the rounded totals so line items always reconcile to
  // the subtotal. `toPrecision(15)` drops the binary noise first, so 2.675
  // rounds to 2.68 where `toFixed` gives 2.67.
  const roundToCents = (val: number) => {
    const cents = Number((safeNumber(val) * 100).toPrecision(15));
    return (Math.sign(cents) * Math.round(Math.abs(cents))) / 100;
  };

  const updatedDetails = values.details.map((item) => ({
    ...item,
    total: roundToCents(safeNumber(item.amount) * safeNumber(item.quantity)),
  }));

  const subtotal = roundToCents(
    updatedDetails.reduce((acc, item) => acc + safeNumber(item.total), 0),
  );

  // Each figure is rounded before the next builds on it, so the lines shown
  // add up to the total to the cent.
  const discountAmount = roundToCents(
    values.discountType === InvoiceDiscountType.PERCENTAGE
      ? subtotal * asPercent(values.discount) * -1
      : safeNumber(values.discount) * -1,
  );

  const baseForTaxFee = roundToCents(subtotal + discountAmount);

  const taxAmount = roundToCents(
    values.taxType === InvoiceDiscountType.PERCENTAGE
      ? baseForTaxFee * asPercent(values.tax)
      : safeNumber(values.tax),
  );

  const baseForServiceFee = roundToCents(baseForTaxFee + taxAmount);

  let serviceFeeAmount = 0;
  if (
    values.serviceFeeType === InvoiceDiscountType.CREDIT_CARD &&
    serviceFeeConfig
  ) {
    const adderDollars = safeNumber(cardFeeAdderCents) / 100;
    const totalPercent =
      safeNumber(cardFeePercent) + safeNumber(platformFeePercent);
    const grossedUp =
      (safeNumber(baseForServiceFee) + adderDollars) / (1 - totalPercent);
    serviceFeeAmount = roundToCents(grossedUp - baseForServiceFee);
  } else {
    serviceFeeAmount = roundToCents(
      values.serviceFeeType === InvoiceDiscountType.PERCENTAGE
        ? baseForServiceFee * asPercent(values.serviceFee)
        : safeNumber(values.serviceFee),
    );
  }

  return {
    details: updatedDetails,
    subtotal,
    discountAmount,
    taxAmount,
    serviceFeeAmount,
    total: roundToCents(baseForServiceFee + serviceFeeAmount),
  };
}

export function formatInvoiceInputForCreateEdit(
  invoice: FormikInvoice,
  transformCustomFieldsToArray: (customFields: CustomFieldsRecord) => any[],
) {
  return {
    invoiceNumber: invoice.invoiceNumber,
    invoiceName: invoice.invoiceName,
    purchaseOrderId: invoice.purchaseOrderId,
    primaryRecipientId: invoice.primaryRecipient?.id,
    secondaryRecipientIds:
      invoice.secondaryRecipients?.map((recipient: any) => recipient.id) ?? [],
    invoiceDate: invoice.invoiceDate,
    paymentTerms: invoice.paymentTerms,
    subtotal: Number(invoice.subtotal),
    discount: Number(invoice.discount),
    discountType: invoice.discountType,
    discountAmount: Number(invoice.discountAmount),
    serviceFee: Number(invoice.serviceFee),
    serviceFeeType: invoice.serviceFeeType,
    serviceFeeAmount: Number(invoice.serviceFeeAmount),
    tax: Number(invoice.tax),
    taxType: invoice.taxType,
    taxAmount: Number(invoice.taxAmount),
    total: Number(invoice.total),
    status: invoice.status,
    note: invoice.note,
    allocationId: invoice.allocationName?.id,
    customFields: transformCustomFieldsToArray(invoice.customFields ?? {}),
  };
}
